import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import InsightPage, {
  StatRow, StatCard, CopyStats, useChartTheme, PHONE_CHART_HEIGHT, PHONE_CHART_MARGIN, PHONE_X_AXIS, PHONE_Y_AXIS,
} from '../../components/insights/InsightPage';
import DateWindowSelect from '../../components/insights/DateWindowSelect';
import { insightsService } from '../../services/apiService';
import { useInsightData } from '../../hooks/useInsightData';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { formatNumber, plural } from '../../utils/format';

const DEFAULT_BUCKETS = ['30 min', '60 min'];
// Phones drop the repeated " min" so all six buckets fit; the card title says minutes instead
const bareMinutes = (bucket) => String(bucket).replace(' min', '');

const MeetingLengths = () => {
  const isMobile = useIsMobile();
  const t = useChartTheme();
  const [days, setDays] = useState(180);
  const { data, loading, error, reload } = useInsightData(() => insightsService.meetingLengths(days), [days]);
  const d = data || {};
  const distribution = d.distribution || [];

  // "Nobody's ever needed 45" holds only when 45-minute meetings are rarer than both default slots
  const bucket = (label) => distribution.find((r) => r.bucket === label)?.meetings || 0;
  const fortyFiveIsRare = d.total > 0
    && bucket('45 min') < bucket('30 min')
    && bucket('45 min') < bucket('60 min')
    && bucket('45 min') / d.total < 0.1;

  const copy = d.total > 0
    ? [
      `${d.pctDefaultLengths}% of meetings are exactly 30 or 60 minutes.`,
      fortyFiveIsRare && "Nobody's ever needed 45.",
      `The average meeting runs ${plural(d.avgMinutes, 'minute')}. 📅`,
    ].filter(Boolean).join(' ')
    : '';

  return (
    <InsightPage
      title="Meeting length reality check"
      description="How long meetings actually run, and the tyranny of the default slot."
      controls={<DateWindowSelect value={days} onChange={setDays} />}
      loading={loading}
      error={error}
      onRetry={reload}
      ready={data != null}
      empty={!d.total}
      emptyMessage="No meetings booked in this window"
    >
      <StatRow>
        <StatCard xs={4} label="Average length" value={`${d.avgMinutes} min`} variant="primary" />
        <StatCard xs={4} label="Exactly 30 or 60 min" value={`${d.pctDefaultLengths}%`} />
        <StatCard xs={4} label="Meetings analysed" value={formatNumber(d.total)} />
      </StatRow>

      <Card className="shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Meeting length distribution{isMobile ? ' (minutes)' : ''}</h5>
          <small className="text-muted">The 30 and 60 minute slots are highlighted.</small>
        </Card.Header>
        <Card.Body>
          <ResponsiveContainer width="100%" height={isMobile ? PHONE_CHART_HEIGHT : 400}>
            <BarChart data={distribution} margin={isMobile ? PHONE_CHART_MARGIN : { top: 10, right: 30, left: 10, bottom: 20 }}>
              <CartesianGrid {...t.grid} />
              <XAxis dataKey="bucket" {...t.xAxis} {...(isMobile ? { ...PHONE_X_AXIS, tickFormatter: bareMinutes } : {})} />
              <YAxis allowDecimals={false} {...t.yAxis} {...(isMobile ? PHONE_Y_AXIS : {})} />
              <Tooltip {...t.tooltip} formatter={(v) => formatNumber(v)} />
              <Bar dataKey="meetings" name="Meetings">
                {distribution.map((r) => (
                  <Cell key={r.bucket} fill={DEFAULT_BUCKETS.includes(r.bucket) ? t.colors.emphasis : t.colors.neutral} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {copy && <CopyStats text={copy} />}
    </InsightPage>
  );
};

export default MeetingLengths;
